export type Role = 'teacher' | 'school_admin';

export const SCHOOL_ADMIN_ROLE: Role = 'school_admin';

export type UserProfile = {
  schoolId: string;
  id: string;
  email: string;
  name: string;
  role: 'teacher' | 'user' | 'admin';
  referredToName: string;
  tosAgreedAt: string;
  isSetupComplete: boolean;
  isSpark: boolean;
  emailVerified?: boolean;
  schoolName?: string;
};

export type Period = {
  id: string;
  schoolId: string;
  teacherId: string;
  name: string;
  shortName: string;
  students: Student[];
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  archivedAt?: string | null;
};

export type Student = {
  id: string;
  schoolId: string;
  voiceName: string;
  lastName: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

// MEETINGS & BLOCKS TYPES

/** Per-meeting processing status. A meeting exists before its content does. */
export type MeetingReviewStatus = 'processing' | 'failed' | 'content';

/** Aggregated per period+local-date. Priority when computed server-side:
 *  active > idle > processing > failed > processed. */
export type ReviewDayStatus = 'active' | 'idle' | 'processing' | 'processed' | 'failed';

export type ReviewMeetingStatusInfo = {
  meetingId: string;
  startedAt: string;
  endedAt: string;
  status: MeetingReviewStatus;
};

/** Shared response envelope inherited by every `/review/get*` endpoint. */
export type ReviewEnvelope<TMeeting> = {
  meetings: TMeeting[];
  streamed: boolean;
  status: ReviewDayStatus | null;
  /** ISO datetime; present IFF `status === 'idle'`. */
  scheduledCloseAt?: string;
};

/** A per-block class summary, from the `getClassSummary` blocks extension. */
export type ReviewBlock = {
  blockId: string;
  subject: string | null;
  ordinal: number;
  startedAt: string;
  endedAt: string;
  markdown: string;
};

//ACTION ITEM TYPES

export type ActionItemsScorecardType =
  | 'warned_students'
  | 'praised_students'
  | 'absent_students'
  | 'class_summary'
  | 'reminders';

export type ActionStatus = 'new' | 'completed' | 'dismissed';

export type ActionItem = {
  id: string;
  title: string;
  type: ActionItemsScorecardType;
  status: ActionStatus;
  message?: string;
  date?: Date;
  studentId?: string;
  studentVoiceName?: string;
  insightId?: string;
};

// REVIEW TYPES

export type ScorecardType = 'warnings' | 'praises' | 'absentees' | 'attendance';

export type AttendanceStatus = 'present' | 'absent' | 'unsure';

export type FetchStatus = 'initial' | 'drafting' | 'success' | 'fail';

export type StudentWithCount = {
  student: Student;
  count: number;
};

export type StudentWithAttendance = {
  student: Student;
  status: AttendanceStatus;
};

export type AttendanceData = {
  attendanceType: AttendanceStatus;
  title: string;
  data: StudentWithAttendance[];
};

// SETUP PROFILE TYPES

export type SetupData = {
  referredToName: string;
  schoolName: string;
  location: string;
  teachingStyle: 'single' | 'multiple' | 'unknown';
  tosAgreedAt: string;
  referralSource: string;
  firstClassName: string;
  firstShortName: string;
  studentVoiceNames: string[];
};
